import React, { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Modal } from '../Modal/Modal';
import HomepageAdsAPI, { LiveHomepageAd } from '../../api/HomepageAdsAPI';
import { sanitizeHomepageAdHtml } from '../../utils/homepageAdHtmlSanitize';
import { useCrispChat } from '../Crisp/CrispProvider';
import { ONBOARDING_BRAND } from '../OnboardingFlow/onboardingBrand';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import './HomeAdModal.css';

interface HomeAdModalProps {
  ad: LiveHomepageAd | null;
  onClose: () => void;
}

const HomeAdModal: React.FC<HomeAdModalProps> = ({ ad, onClose }) => {
  const { openChat } = useCrispChat();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!ad) {
      return;
    }
    datadogRum.addAction('Homepage Ad - Opened', { adId: ad.id, campaignName: ad.name });
    void HomepageAdsAPI.recordEngagement(ad.id).catch((error) => {
      console.error('Failed to record homepage ad engagement', { adId: ad.id, error });
    });
  }, [ad]);

  if (!ad) {
    return null;
  }

  const handleOpenChat = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await HomepageAdsAPI.registerInterest(ad.id);
      datadogRum.addAction('Homepage Ad - Chat Clicked', {
        adId: ad.id,
        campaignName: ad.name,
        alreadyInterested: response.alreadyInterested,
      });
    } catch (error) {
      console.error('Failed to register homepage ad interest', { adId: ad.id, error });
    }
    onClose();
    openChat();
  };

  return (
    <Modal modalTitle={ad.modalTitle} canClose onClose={onClose}>
      <div className="mx-auto max-w-prose space-y-6">
        <div
          className={`${ONBOARDING_BRAND.scope} homepage-ad-body text-[15px] leading-relaxed text-muted-foreground [&_a]:font-medium [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_b]:font-semibold [&_b]:text-foreground [&_em]:italic [&_h1]:mb-2 [&_h1]:mt-5 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:leading-snug [&_h1]:text-foreground [&_h1:first-child]:mt-0 [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:text-foreground [&_h2:first-child]:mt-0 [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:leading-snug [&_h3]:text-foreground [&_h3:first-child]:mt-0 [&_i]:italic [&_li]:leading-relaxed [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-5 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_strong]:text-foreground`}
          dangerouslySetInnerHTML={{ __html: sanitizeHomepageAdHtml(ad.modalBody) }}
        />
        <div className="flex justify-center">
          <PrimaryButton
            text={ad.ctaText}
            fn={() => void handleOpenChat()}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default HomeAdModal;
